import React, { useState, useEffect, useMemo } from 'react';
import { IoBusiness, IoClose, IoSparkles, IoTime, IoTrendingUp } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
import { getCityNameKey } from '../../city/constants';
import { BASE_KEYS, BASE_OCCUPATION_HOURS } from '../constants';
import { maskNickname } from '../../../utils/maskNickname';

function getErrorKey(errorCode) {
  if (errorCode === 'MAX_BASE_REACHED') return BASE_KEYS.MAX_REACHED;
  if (errorCode === 'ALREADY_OCCUPYING_BASE') return BASE_KEYS.ALREADY_OCCUPYING;
  if (errorCode === 'BASE_FULL') return BASE_KEYS.FULL;
  if (errorCode === 'INSUFFICIENT_TICKETS') return BASE_KEYS.INSUFFICIENT_TICKETS;
  if (errorCode === 'NOT_GENESIS') return BASE_KEYS.NOT_GENESIS;
  if (errorCode === 'ALREADY_RESERVED') return BASE_KEYS.ALREADY_RESERVED;
  if (errorCode === 'CITY_RESERVE_FULL') return BASE_KEYS.CITY_RESERVE_FULL;
  if (errorCode === 'USER_BANNED') return BASE_KEYS.USER_BANNED;
  return BASE_KEYS.ERROR;
}

function toMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (value.seconds != null) return value.seconds * 1000;
  if (value._seconds != null) return value._seconds * 1000;
  if (typeof value === 'number') return value;
  return 0;
}

function formatRemainingMs(expiresAt, t) {
  const remainingMs = Math.max(0, toMillis(expiresAt) - Date.now());
  const totalMinutes = Math.ceil(remainingMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0) return t('base_duration_minutes', { minutes });
  if (minutes <= 0) return t('base_duration_hours', { hours });
  return t('base_duration_hm', { hours, minutes });
}

function calcEstimatedEarning(capturedAt, rate, limitHours, multiplier, now) {
  const capturedMs = toMillis(capturedAt);
  if (!capturedMs) return 0;
  const elapsedMin = Math.min(limitHours * 60, Math.max(0, (now - capturedMs) / 60000));
  return Math.round(elapsedMin * rate * multiplier * 10000) / 10000;
}

export default function BaseDetailModal({
  base,
  myActiveBasesCount,
  config,
  piUser,
  loading,
  error,
  onClose,
  onCapture,
  onReserve,
  onCancelReservation,
  ownsGenesis,
  myReservation,
  myReservations,
  showConfirm,
  ticketCount
}) {
  const { t } = useTranslation();
  const [currentTime, setCurrentTime] = useState(Date.now());

  const isMyBase = base?.isMyBase === true;
  const rate = config?.rate ?? 0;
  const hours = config?.limitHours || BASE_OCCUPATION_HOURS;
  const myStatus = base?.currentBase ?? null;
  const totalBonusPercent = base?.totalBonusPercent ?? 0;

  useEffect(() => {
    if (!isMyBase) return;
    const timer = setInterval(() => setCurrentTime(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [isMyBase]);

  const myEarning = useMemo(() => {
    if (!isMyBase || !myStatus?.capturedAt) return null;
    // 정산도 현재 장착 기반이므로 예상 SPOT도 현재 장착 상태로 표시
    // → 장착 해제 시 즉시 예상치가 줄어 "장착 유지 = 더 많은 SPOT" 체감 가능
    const liveMultiplier = 1 + totalBonusPercent / 100;
    const rateSnapshot = Number(myStatus.rateSnapshot);
    const limitSnapshot = Number(myStatus.limitHoursSnapshot);
    return calcEstimatedEarning(
      myStatus.capturedAt,
      myStatus.rateSnapshot != null && Number.isFinite(rateSnapshot) ? rateSnapshot : rate,
      myStatus.limitHoursSnapshot != null && Number.isFinite(limitSnapshot) && limitSnapshot > 0 ? limitSnapshot : hours,
      liveMultiplier,
      currentTime
    );
  }, [isMyBase, myStatus, totalBonusPercent, rate, hours, currentTime]);

  const myRemaining = useMemo(() => {
    if (!isMyBase || !myStatus?.expiresAt) return null;
    return formatRemainingMs(myStatus.expiresAt, t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMyBase, myStatus, currentTime]);

  if (!base) return null;

  const cityName = t(getCityNameKey(base.cityId));
  const reservations = Array.isArray(myReservations)
    ? myReservations
    : (myReservation ? [myReservation] : []);
  const cityReservation = reservations.find((item) => item?.cityId === base.cityId) || null;
  const maxBasesPerUser = config?.maxBasesPerUser ?? 3;
  const atMaxBases = !isMyBase && myActiveBasesCount >= maxBasesPerUser;
  const ticketCost = config?.captureTicketCost ?? 1;
  const insufficientTickets = !isMyBase && typeof ticketCount === 'number' && ticketCount < ticketCost;
  const disabled = loading || base.isFull || atMaxBases || isMyBase || !piUser?.uid || insufficientTickets;
  const slots = Array.from({ length: base.slotsPerCity }, (_, slotIndex) => {
    const occupied = (base.statuses || []).find((status) => status.slotIndex === slotIndex);
    return { slotIndex, occupied };
  });

  const handleCapture = () => {
    if (!piUser?.uid) return;
    // 티켓 사전 검증 (추가 함수 호출 없이 이미 보유한 ticketCount 활용)
    if (typeof ticketCount === 'number' && ticketCount < ticketCost) {
      if (showConfirm) {
        showConfirm(t(BASE_KEYS.INSUFFICIENT_TICKETS), null, null, 'error');
      }
      return;
    }
    const doCapture = () => Promise.resolve(onCapture(base.cityId)).catch(() => {});
    if (showConfirm) {
      showConfirm(t(BASE_KEYS.CLAIM_CONFIRM, { name: cityName }), doCapture, null, 'warning');
    } else {
      if (window.confirm(t(BASE_KEYS.CLAIM_CONFIRM, { name: cityName }))) doCapture();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 14000,
        background: 'rgba(17, 24, 39, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '18px 18px calc(18px + 64px + env(safe-area-inset-bottom))'
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 380,
          maxHeight: '100%',
          background: '#FFFFFF',
          borderRadius: 20,
          overflow: 'hidden',
          boxShadow: '0 22px 60px rgba(0, 0, 0, 0.22)',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 이미지 - 고정 높이 */}
        <div style={{ position: 'relative', height: 150, flexShrink: 0, background: 'linear-gradient(135deg, #6C5CE7 0%, #14B8A6 100%)' }}>
          {base.imageUrl || base.thumbnailUrl ? (
            <img
              src={base.imageUrl || base.thumbnailUrl}
              alt={cityName}
              style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.82 }}
            />
          ) : (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IoBusiness size={54} color="#FFFFFF" />
            </div>
          )}
          <button
            type="button"
            onClick={onClose}
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              width: 34,
              height: 34,
              border: 'none',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.92)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
            aria-label={t(BASE_KEYS.CLOSE)}
          >
            <IoClose size={20} />
          </button>
        </div>

        {/* 스크롤 가능한 콘텐츠 영역 */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 18px 0', minHeight: 0 }}>
          <h3 style={{ margin: 0, color: '#111827', fontSize: '1.15rem', fontWeight: 900 }}>
            {t(BASE_KEYS.DETAIL_TITLE, { name: cityName })}
          </h3>
          <p style={{ margin: '8px 0 0', color: '#6B7280', fontSize: '0.85rem', lineHeight: 1.5 }}>
            {t(BASE_KEYS.SUBTITLE)}
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 16 }}>
            <div style={{ background: '#F9FAFB', borderRadius: 12, padding: 12 }}>
              <div style={{ color: '#6B7280', fontSize: '0.7rem', fontWeight: 800 }}>{t(BASE_KEYS.OCCUPIED_COUNT, { occupied: base.occupiedCount, total: base.slotsPerCity })}</div>
              <div style={{ marginTop: 5, color: '#111827', fontSize: '1.05rem', fontWeight: 900 }}>{base.occupiedCount} / {base.slotsPerCity}</div>
            </div>
            <div style={{ background: '#F9FAFB', borderRadius: 12, padding: 12 }}>
              <div style={{ color: '#6B7280', fontSize: '0.7rem', fontWeight: 800 }}>{t(BASE_KEYS.HOURS)}</div>
              <div style={{ marginTop: 5, color: '#111827', fontSize: '1.05rem', fontWeight: 900 }}>{t('base_duration_hours', { hours })}</div>
            </div>
          </div>

          <div style={{
            marginTop: 12,
            borderRadius: 12,
            padding: 12,
            background: (base.totalBonusPercent > 0) ? '#FEF3C7' : '#F3F4F6',
            color: (base.totalBonusPercent > 0) ? '#92400E' : '#6B7280',
            fontSize: '0.82rem',
            fontWeight: 800,
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }}>
            {(base.totalBonusPercent > 0) && <IoSparkles size={16} />}
            {(base.totalBonusPercent > 0)
              ? (() => {
                  const parts = [];
                  if (base.slotBonusPercent > 0) parts.push(t(BASE_KEYS.BONUS_CITY, { percent: base.slotBonusPercent }));
                  if (base.matchingBonusPercent > 0) parts.push(t(BASE_KEYS.BONUS_MATCHING, { percent: base.matchingBonusPercent }));
                  return `${parts.join(' / ')} = +${base.totalBonusPercent}%`;
                })()
              : t(BASE_KEYS.BONUS_NONE)}
          </div>

          {isMyBase && myStatus && (
            <div style={{
              marginTop: 12,
              borderRadius: 12,
              padding: 12,
              background: 'linear-gradient(135deg, #EDE9FE 0%, #CCFBF1 100%)',
              border: '1px solid #C4B5FD'
            }}>
              <div style={{ color: '#5B21B6', fontSize: '0.72rem', fontWeight: 900, marginBottom: 8 }}>
                {t(BASE_KEYS.MY_STATUS)}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#6B7280', fontSize: '0.68rem', fontWeight: 700 }}>
                    <IoTime size={12} />
                    {t(BASE_KEYS.MY_REMAINING)}
                  </div>
                  <div style={{ marginTop: 3, color: '#111827', fontSize: '1rem', fontWeight: 900 }}>
                    {myRemaining}
                  </div>
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#6B7280', fontSize: '0.68rem', fontWeight: 700 }}>
                    <IoTrendingUp size={12} />
                    {t(BASE_KEYS.MY_EARNING)}
                  </div>
                  <div style={{ marginTop: 3, color: '#111827', fontSize: '1rem', fontWeight: 900 }}>
                    {myEarning !== null ? `${myEarning.toFixed(4)} SPOT` : '-'}
                  </div>
                </div>
              </div>
              <div style={{ marginTop: 6, color: '#7C3AED', fontSize: '0.68rem', fontWeight: 700 }}>
                #{(myStatus.slotIndex ?? 0) + 1} {t(BASE_KEYS.MY_SLOT)}
              </div>
            </div>
          )}

          <div style={{ marginTop: 8, color: '#9CA3AF', fontSize: '0.75rem' }}>
            {t(BASE_KEYS.RATE, { rate })}
          </div>

          <div style={{ marginTop: 4, color: '#9CA3AF', fontSize: '0.75rem' }}>
            {t(BASE_KEYS.TICKET_COST, { count: ticketCost })}
          </div>

          <div style={{ marginTop: 14, paddingBottom: 8 }}>
            <div style={{ color: '#111827', fontSize: '0.82rem', fontWeight: 900, marginBottom: 8 }}>
              {t(BASE_KEYS.OCCUPANTS)}
            </div>
            <div style={{ display: 'grid', gap: 6, maxHeight: 240, overflowY: 'auto', paddingRight: 2 }}>
              {slots.map(({ slotIndex, occupied }) => (
                <div
                  key={slotIndex}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 8,
                    borderRadius: 10,
                    background: occupied ? '#F9FAFB' : '#F3F4F6',
                    padding: '8px 10px',
                    fontSize: '0.75rem'
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: occupied ? '#111827' : '#9CA3AF', fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      #{slotIndex + 1}{' '}
                      {occupied ? (occupied.reservedUserId ? `${maskNickname(occupied.ownerName)} · ${t(BASE_KEYS.RESERVED_SLOT)}` : maskNickname(occupied.ownerName)) : t(BASE_KEYS.SLOT_EMPTY)}
                    </div>
                    {occupied && (
                      <div style={{ marginTop: 2, color: '#6B7280', fontSize: '0.68rem', fontWeight: 700 }}>
                        {t(BASE_KEYS.TIME_LEFT, { time: formatRemainingMs(occupied.expiresAt, t) })}
                      </div>
                    )}
                  </div>
                  {occupied?.cityOwnedBonusMultiplier > 1 && (() => {
                    const bonusPct = Math.round((occupied.cityOwnedBonusMultiplier - 1) * 100);
                    return (
                      <span title={t(BASE_KEYS.BONUS_SNAPSHOT_NOTICE)} style={{
                        flexShrink: 0,
                        borderRadius: 999,
                        background: '#FEF3C7',
                        color: '#92400E',
                        padding: '3px 6px',
                        fontSize: '0.65rem',
                        fontWeight: 900
                      }}>
                        {`+${bonusPct}%`}
                      </span>
                    );
                  })()}
                </div>
              ))}
            </div>
            <div style={{ marginTop: 6, color: '#9CA3AF', fontSize: '0.66rem' }}>
              {t(BASE_KEYS.BONUS_SNAPSHOT_NOTICE)}
            </div>
          </div>
        </div>

        {/* 고정 푸터: 에러 + 점유 버튼 (스크롤에 밀리지 않음) */}
        <div style={{
          flexShrink: 0,
          padding: '8px 18px',
          paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
          background: '#FFFFFF',
          borderTop: '1px solid #F3F4F6'
        }}>
          {error && (
            <div style={{ marginBottom: 8, color: '#EF4444', fontSize: '0.78rem', fontWeight: 700 }}>
              {t(getErrorKey(error.code || error.message))}
            </div>
          )}
          <button
            type="button"
            disabled={disabled}
            onClick={handleCapture}
            style={{
              width: '100%',
              border: 'none',
              borderRadius: 12,
              padding: '13px 14px',
              fontSize: '0.92rem',
              fontWeight: 900,
              color: '#FFFFFF',
              background: disabled ? '#9CA3AF' : 'linear-gradient(135deg, #6C5CE7 0%, #14B8A6 100%)',
              cursor: disabled ? 'not-allowed' : 'pointer'
            }}
          >
            {!piUser?.uid
              ? t(BASE_KEYS.LOGIN_REQUIRED)
              : isMyBase
                ? t(BASE_KEYS.MY_BASE)
                : atMaxBases
                  ? t(BASE_KEYS.MAX_REACHED)
                  : base.isFull
                    ? t(BASE_KEYS.FULL)
                    : insufficientTickets
                      ? t(BASE_KEYS.INSUFFICIENT_TICKETS)
                      : loading
                        ? t(BASE_KEYS.CLAIMING)
                        : t(BASE_KEYS.CLAIM)}
          </button>
          {base.isFull && !ownsGenesis && !isMyBase && (
            <div style={{ marginTop: 8, color: '#5B21B6', fontSize: '0.75rem', fontWeight: 700, lineHeight: 1.45 }}>
              {t(BASE_KEYS.RESERVE_GENESIS_HINT)}
            </div>
          )}
          {ownsGenesis && base.isFull && !isMyBase && piUser?.uid && (
            cityReservation ? (
              <button
                type="button"
                disabled={loading}
                onClick={() => {
                  const run = () => Promise.resolve(onCancelReservation(base.cityId)).catch(() => {});
                  if (showConfirm) showConfirm(t(BASE_KEYS.RESERVE_CANCEL), run, null, 'warning');
                  else run();
                }}
                style={{
                  width: '100%',
                  marginTop: 8,
                  border: 'none',
                  borderRadius: 12,
                  padding: '12px 14px',
                  fontSize: '0.88rem',
                  fontWeight: 900,
                  color: '#5B21B6',
                  background: '#EDE9FE',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {t(BASE_KEYS.RESERVE_CANCEL)}
              </button>
            ) : (
              <button
                type="button"
                disabled={loading || atMaxBases || insufficientTickets}
                onClick={() => {
                  if (insufficientTickets) return;
                  const run = () => Promise.resolve(onReserve(base.cityId)).catch(() => {});
                  if (showConfirm) showConfirm(t(BASE_KEYS.RESERVE_CONFIRM, { name: cityName }), run, null, 'warning');
                  else run();
                }}
                style={{
                  width: '100%',
                  marginTop: 8,
                  border: 'none',
                  borderRadius: 12,
                  padding: '12px 14px',
                  fontSize: '0.88rem',
                  fontWeight: 900,
                  color: '#FFFFFF',
                  background: (loading || atMaxBases || insufficientTickets) ? '#9CA3AF' : '#6C5CE7',
                  cursor: (loading || atMaxBases || insufficientTickets) ? 'not-allowed' : 'pointer'
                }}
              >
                {t(BASE_KEYS.RESERVE)}
              </button>
            )
          )}
          {cityReservation && (
            <div style={{ marginTop: 8, color: '#5B21B6', fontSize: '0.75rem', fontWeight: 800 }}>
              {t(BASE_KEYS.RESERVE_WAITING)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
